import React, { useId } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../utils/cn';

interface ProfileFieldProps {
  id?: string;
  label: string;
  value: string;
  onChange?: (value: string) => void;
  type?: 'text' | 'tel' | 'email';
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  error?: string;
  helperText?: string;
  icon?: LucideIcon;
  className?: string;
}

export const ProfileField: React.FC<ProfileFieldProps> = ({
  id,
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  readOnly = false,
  required = false,
  error,
  helperText,
  icon: Icon,
  className,
}) => {
  const generatedId = useId();
  const inputId = id || generatedId;

  return (
    <div className={cn('block', className)}>
      <label htmlFor={inputId} className="mb-1 block text-sm font-medium text-gray-700">
        {label}
      </label>
      <div className="relative">
        {Icon ? (
          <Icon
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            aria-hidden="true"
          />
        ) : null}
        <input
          id={inputId}
          type={type}
          value={value}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          placeholder={placeholder}
          readOnly={readOnly}
          required={required && !readOnly}
          aria-invalid={Boolean(error)}
          aria-describedby={
            error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
          }
          className={cn(
            'w-full rounded-lg border py-2 text-sm',
            Icon ? 'pl-10 pr-3' : 'px-3',
            readOnly
              ? 'cursor-default border-gray-200 bg-slate-50 text-gray-700'
              : 'border-gray-300 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-blue-500',
            error && !readOnly ? 'border-red-300 focus:ring-red-500' : null
          )}
        />
      </div>
      {error ? (
        <p id={`${inputId}-error`} className="mt-1 text-sm text-red-600">
          {error}
        </p>
      ) : null}
      {!error && helperText ? (
        <p id={`${inputId}-helper`} className="mt-1 text-xs text-gray-500">
          {helperText}
        </p>
      ) : null}
    </div>
  );
};
